import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Menu,
  MenuItem,
  Box,
  Container,
  Avatar,
  Stack,
  alpha,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useColorScheme } from '@mui/material/styles';
import { useSettingsContext } from "../settings";
import { BaseOption } from "../settings/drawer/base-option";
import ThemeToggle from '../ThemeToggle';
import { createStartup } from "../../store/startup/startupSlice";
import { logoutAndRedirect } from "../../store/slices/authSlice";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdown1Anchor, setDropdown1Anchor] = useState(null);
  const [dropdown2Anchor, setDropdown2Anchor] = useState(null);
  const [dropdown1Open, setDropdown1Open] = useState(false);
  const [dropdown2Open, setDropdown2Open] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const settings = useSettingsContext();

  const { isAuthenticated, logout } = useAuth();
  const { mode, setMode, systemMode } = useColorScheme();

  useEffect(() => {
    if (mode === 'system' && systemMode) {
      settings.setState({ colorScheme: systemMode });
    }
  }, [mode, systemMode]);

  const handleDropdown1Toggle = (event) => setDropdown1Anchor(event.currentTarget);
  const handleDropdown2Toggle = (event) => setDropdown2Anchor(event.currentTarget);
  const closeDropdown1 = () => setDropdown1Anchor(null);
  const closeDropdown2 = () => setDropdown2Anchor(null);
  const toggleMobileMenu = () => setMobileOpen(!mobileOpen);
  const toggleMobileDropdown1 = () => setDropdown1Open(!dropdown1Open);
  const toggleMobileDropdown2 = () => setDropdown2Open(!dropdown2Open);
  const openProfileMenu = (event) => setProfileMenuAnchor(event.currentTarget);
  const closeProfileMenu = () => setProfileMenuAnchor(null);

  const handleLogout = () => {
    dispatch(logoutAndRedirect());
    closeProfileMenu();
  };

  const dispatch = useDispatch();

  const beginCampaign = async () => {
    if (!isAuthenticated) {
        window.location.href = "/login";
        return;
    }

    try {
        const response = await dispatch(createStartup()).unwrap();
        if (response.code === "00000") {
            window.location.href = "/profil#campaigns";
        } else {
            window.location.href = "/profil#entrepreneur";
        }
    } catch (error) {
        window.location.href = "/profil";
    }
};

  return (
    <AppBar
      position="sticky"
      sx={{
        backdropFilter: 'blur(6px)',
        backgroundColor: (theme) => alpha(theme.palette.background.default, 0.8),
        boxShadow: (theme) => `inset 0px -1px 1px ${theme.palette.grey[100]}`
      }}
    >
      <Container maxWidth="lg">
        <Toolbar
          disableGutters
          sx={{
            height: 64,
            justifyContent: 'space-between'
          }}
        >
          <Typography
            component={Link}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <img
              src="/img/adventureLogo.png"
              alt="Logo"
              style={{ height: "40px" }}
            />
          </Typography>

          {/* Desktop Menu */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              display: { xs: "none", md: "flex" }
            }}
          >

            <ThemeToggle
              onChange={() => {
                const newMode = mode === 'light' ? 'dark' : 'light';
                setMode(newMode);
                settings.setState({ colorScheme: newMode });
              }}
            />

            {isAuthenticated ? (
              <>
                <IconButton
                  onClick={openProfileMenu}
                  sx={{
                    width: 40,
                    height: 40,
                    ml: 1
                  }}
                >
                  <Avatar
                    src="/img/user-avatar.png"
                    alt="User Avatar"
                    sx={{
                      width: 32,
                      height: 32
                    }}
                  />
                </IconButton>
                <Menu
                  anchorEl={profileMenuAnchor}
                  open={Boolean(profileMenuAnchor)}
                  onClose={closeProfileMenu}
                  PaperProps={{
                    sx: {
                      width: 200,
                      mt: 2
                    }
                  }}
                >
                  {/*<MenuItem component={Link} to="/profil" onClick={closeProfileMenu}>
                    Profil
                  </MenuItem> */}
                  <MenuItem onClick={handleLogout}>
                    Çıkış Yap
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button
                  component={Link}
                  to="/giris"
                  variant="outlined"
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'primary.lighter'
                    }
                  }}
                >
                  Giriş Yap
                </Button>
                <Button
                  component={Link}
                  to="/kayit-ol"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark'
                    }
                  }}
                >
                  Kayıt Ol
                </Button>
              </Stack>
            )}
          </Stack>

          {/* Mobile Menu Toggle */}
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <IconButton
              onClick={toggleMobileMenu}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: '100%',
            bgcolor: 'background.paper'
          }
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography component={Link} to="/" sx={{ textDecoration: 'none' }}>
            <img
              src="/img/adventureLogo.png"
              alt="Logo"
              style={{ height: "40px" }}
            />
          </Typography>
          <IconButton onClick={toggleMobileMenu}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        <List sx={{ pt: 0 }}>
          <ListItem button onClick={toggleMobileDropdown1}>
            <ListItemText primary="Yatırımcı" />
            {dropdown1Open ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={dropdown1Open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={Link}
                to="/yatirim-yap"
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Yatırım Yap" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/melek-yatirimci"
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Melek Yatırımcı" />
              </ListItem>
              <ListItem
                button
                component={Link}
                to="/nasil-yatirimci-olunur"
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Nasıl Yatırımcı Olunur" />
              </ListItem>
            </List>
          </Collapse>

          <ListItem button onClick={toggleMobileDropdown2}>
            <ListItemText primary="Girişimci" />
            {dropdown2Open ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
          <Collapse in={dropdown2Open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={Link}
                to="/nasil-girisimci-olunur"
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Nasıl Girişimci Olunur?" />
              </ListItem>
              <ListItem
                button
                component={Link}
                onClick={beginCampaign}
                sx={{ pl: 4 }}
              >
                <ListItemText primary="Kampanya Başlat" />
              </ListItem>
            </List>
          </Collapse>

          <Divider sx={{ my: 2 }} />

          {isAuthenticated ? (
            <>
              <ListItem button component={Link} to="/profil">
                <ListItemText primary="Profil" />
              </ListItem>
              <ListItem button onClick={handleLogout}>
                <ListItemText primary="Çıkış Yap" />
              </ListItem>
            </>
          ) : (
            <>
              <ListItem button component={Link} to="/giris">
                <ListItemText primary="Giriş Yap" />
              </ListItem>
              <ListItem button component={Link} to="/kayit-ol">
                <ListItemText primary="Kayıt Ol" />
              </ListItem>
            </>
          )}
        </List>
      </Drawer>
    </AppBar>
  );
}
